/**
 * CategoryPieChart
 *
 * 카테고리 분포 파이차트
 * - 범례 제거로 차트 크기 증가
 * - 3% 미만 항목들을 "기타"로 그룹핑
 */

import { useState, useEffect } from 'react'
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from 'recharts'
import type { CategoryStat } from '../../types/Statistics'

interface CategoryPieChartProps {
  data: CategoryStat[]
  onCategoryClick: (stat: CategoryStat) => void
}

// 차트 색상 팔레트
const COLORS = [
  '#3b82f6', // primary-500
  '#10b981', // green-500
  '#f59e0b', // amber-500
  '#ef4444', // red-500
  '#8b5cf6', // violet-500
  '#ec4899', // pink-500
  '#06b6d4', // cyan-500
  '#f97316', // orange-500
]

export function CategoryPieChart({ data, onCategoryClick }: CategoryPieChartProps) {
  // 반응형 차트 높이
  const [chartHeight, setChartHeight] = useState(400)

  useEffect(() => {
    const updateChartSize = () => {
      setChartHeight(window.innerWidth < 640 ? 320 : 400)
    }
    updateChartSize()
    window.addEventListener('resize', updateChartSize)
    return () => window.removeEventListener('resize', updateChartSize)
  }, [])

  // 총 지출 계산
  const totalExpense = data.reduce((sum, stat) => sum + stat.amount, 0)

  // 3% 기준으로 분리
  const mainCategories = data.filter(stat => stat.percentage >= 3)
  const minorCategories = data.filter(stat => stat.percentage < 3)

  let displayData = [...mainCategories]

  // 3% 미만 항목들을 "기타"로 묶기
  if (minorCategories.length > 0) {
    const othersAmount = minorCategories.reduce((sum, stat) => sum + stat.amount, 0)
    const othersPercentage = (othersAmount / totalExpense) * 100
    const othersTransactions = minorCategories.flatMap(stat => stat.transactions)

    // 가상 "기타" CategoryStat 생성
    const othersStat: CategoryStat = {
      categoryId: 'others',
      categoryName: '기타',
      categoryIcon: '📦',
      amount: othersAmount,
      percentage: othersPercentage,
      transactionCount: othersTransactions.length,
      transactions: othersTransactions
    }

    displayData.push(othersStat)
  }

  // 차트용 데이터 변환
  const chartData = displayData.map(stat => ({
    name: `${stat.categoryIcon} ${stat.categoryName}`,
    value: stat.amount,
    stat  // 원본 데이터 보관 ("기타" 클릭 시에도 모달 표시 가능)
  }))

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-4 mb-4">
      <h3 className="text-base sm:text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">
        카테고리별 지출 분포
      </h3>

      {chartData.length > 0 ? (
        <ResponsiveContainer width="100%" height={chartHeight}>
          <PieChart>
            <Pie
              data={chartData}
              cx="50%"
              cy="50%"
              labelLine={false}
              label={(entry) => `${entry.name} (${entry.stat.percentage.toFixed(1)}%)`}
              outerRadius="65%"
              fill="#8884d8"
              dataKey="value"
              onClick={(data) => onCategoryClick(data.stat)}
            >
              {chartData.map((_, index) => (
                <Cell
                  key={`cell-${index}`}
                  fill={COLORS[index % COLORS.length]}
                  className="cursor-pointer hover:opacity-80 transition-opacity"
                />
              ))}
            </Pie>
            <Tooltip
              formatter={(value: number) => `${value.toLocaleString()}원`}
              contentStyle={{
                backgroundColor: 'rgba(255, 255, 255, 0.95)',
                border: '1px solid #e5e7eb',
                borderRadius: '0.5rem'
              }}
            />
            {/* Legend 제거됨 - 차트 크기 증가 */}
          </PieChart>
        </ResponsiveContainer>
      ) : (
        <div className="h-96 flex items-center justify-center text-gray-500 dark:text-gray-400">
          지출 데이터가 없습니다
        </div>
      )}
    </div>
  )
}
